import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import { type Group, groupScheme } from '@/models/group/group-info';
import { generateResponseScheme, type RespondedWith, type ResponseError } from '@/models/response';
import { uploadableScheme } from '@/models/uploadable';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';

export default function useCreateGroup() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<RespondedWith<Group>, ResponseError, CreateGroupRequestData>({
    mutationKey: ['/CreateGroup'],
    mutationFn: (params) =>
      fetcher(
        '/CreateGroup',
        {
          ...params,
          Info: '',
          JoinType: '0',
        },
        {
          schema: generateResponseScheme(groupScheme),
          validResponseCodes: [0],
        },
      ),
    onSuccess: () => queryClient.invalidateQueries(groupsQueryOptions),
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: error ? error.message : t('contact.create-group.create-success'),
      }),
  });
}

export type CreateGroupRequestData = {
  GroupName: string;
  GroupImg?: string;
  UserIDs: string;
};

export const createGroupFormScheme = z.object({
  GroupName: z.string().trim().min(1, 'errors.string.min-length|count=1').max(50, 'errors.string.max-length|count=50'),
  GroupImg: uploadableScheme.optional(),
  UserIDs: z.array(z.number()).min(1),
});

export type CreateGroupFormData = z.infer<typeof createGroupFormScheme>;
